import { useState, useEffect } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { listApproaches, getApproach } from '../api/marketplaceApi';
import type { TemplateApproach } from '../types/templateApproach';
import type { LocalizedString } from 'types/context';
import { resolveLocalizedString } from '../utils/resolveLocalizedString';

export interface UseTemplateApproachesResult {
  approaches: TemplateApproach[];
  loading: boolean;
  error: string | null;
}

/**
 * Fetches approaches with templateFor defined (i.e. sphere-creation templates).
 * Calls listApproaches then fetches each approach's definition in parallel.
 * Limited to 30 approaches (template_grid_page_size default from dynamic config).
 */
export function useTemplateApproaches(limit = 30): UseTemplateApproachesResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [approaches, setApproaches] = useState<TemplateApproach[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!apiClients?.schemaClient || !organizationId) {
      setApproaches([]);
      return;
    }

    let cancelled = false;

    async function fetchTemplates() {
      setLoading(true);
      setError(null);

      try {
        const listResponse = await listApproaches(
          apiClients!.schemaClient,
          { limit },
        );

        const approachList = listResponse.approaches ?? [];

        // Fetch full detail for each approach in parallel to get the version definition.
        const detailResults = await Promise.allSettled(
          approachList.map((a) =>
            getApproach(apiClients!.schemaClient, a.system_name),
          ),
        );

        const templates: TemplateApproach[] = [];

        for (const result of detailResults) {
          if (result.status !== 'fulfilled') continue;

          const { approach } = result.value;
          const version = approach.version ?? approach.latestVersion ?? null;
          const templateFor = version?.definition?.templateFor;

          if (!templateFor) continue; // not a sphere-creation template

          templates.push({
            id: approach.id,
            system_name: approach.system_name,
            name: resolveLocalizedString(approach.name),
            description: resolveLocalizedString(approach.description),
            use_case_tags: approach.use_case_tags ?? [],
            applicability: version?.applicability ?? null,
            templateFor,
            childCount: version?.definition?.children?.length ?? 0,
            factorCount: version?.definition?.statusFactors?.length ?? 0,
            metaspaceCount: version?.definition?.providedMetaspaces?.length ?? 0,
            official: false, // populated in Phase 5 via channel items
            isNew: false,
            // channel: undefined — populated in Phase 5 via subscribed channel items
          });
        }

        if (!cancelled) {
          setApproaches(templates);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load template approaches');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchTemplates();

    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, limit]);

  return { approaches, loading, error };
}
